/**
 * V70 — páginas PÚBLICAS del canal de booking (link/QR, sin login):
 *   /reservar/:key   → agenda pública del negocio + crear reserva
 *   /reserva/:token  → gestionar la reserva propia (ver / cancelar / mover)
 *   /espera/:token   → oferta de la sala de espera (reclamar / rechazar) — V89
 *
 * Se montan FUERA de AuthProvider (patrón LegalRouter/PublicAuthRouter en
 * App.tsx): sin sesión, sin refresh, sin TenantInterceptor. Usan fetch plano
 * contra /api/v1/public/booking/* — NUNCA el cliente `api` autenticado.
 * El backend resuelve el tenant SOLO por la key (o por el token de gestión).
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { API_BASE } from '../lib/api';
import { SpotMapPicker, type RoomLayout, type SpotCell } from './SpotMapPicker';
import { bookingLabels, verticalFromKinds } from './bookingLabels';
import { cancellationPolicyText } from './cancellationPolicy';

type PublicInfo = {
  name: string; logoUrl: string | null; primaryColor: string | null;
  // V89 — si el negocio tiene sala de espera (muestra el botón en clases llenas).
  waitlistEnabled?: boolean;
  // C3 (#285) — sucursales activas para el selector (1 sola → no se muestra).
  branches?: Array<{ id: string; name: string }>;
  // V172 — URL del aviso de privacidad publicado del negocio (la casilla lo enlaza).
  avisoUrl?: string | null;
};
// V88 — disponibilidad por tipo de espacio (lista vacía = clase sin tipos).
// V93 — hasMap: el aula tiene mapa de lugares (el cliente elige el suyo).
type PublicResourceType = {
  id: string; name: string; capacity: number; free: number; hasMap?: boolean;
};
// V93/V94 — plano público del aula en un slot (shape compartido con el staff;
// layout = orientación frente/espejos/entrada, null = sin chrome).
type PublicSpotRoom = {
  roomId: string; roomName: string; freeCount: number;
  spots: SpotCell[]; layout?: RoomLayout;
};
type PublicSlot = {
  slotId: string; offeringName: string; offeringKind: string; offeringColor: string | null;
  startsAt: string; durationMinutes: number; capacity: number; freeCount: number;
  // V74 — instructor del offering (contenido promocional del negocio).
  instructorName?: string | null; instructorBio?: string | null;
  resourceTypes?: PublicResourceType[];
  // Epic Pagos (V101) — precio/modo/política de la clase.
  price?: number | null; currency?: string;
  paymentMode?: 'none' | 'required';
  noShowChargePct?: number; lateCancelWindowHours?: number;
  lateCancelChargePct?: number;
};
// V75 — pregunta configurable del formulario (render público).
type PublicFormField = {
  fieldId: string; label: string; fieldType: 'text' | 'select' | 'date' | 'boolean';
  options: string[] | null; isRequired: boolean; sortOrder: number;
};
type ManageInfo = {
  status: 'BOOKED' | 'CANCELLED' | 'ATTENDED' | 'NO_SHOW';
  customerName: string; startsAt: string; durationMinutes: number;
  offeringName: string; offeringColor: string | null;
  // V88 — snapshot del tipo de espacio de la reserva.
  resourceTypeName?: string | null;
  // V93 — aula (para el plano al reagendar) + lugar de la reserva.
  resourceTypeId?: string | null;
  spotName?: string | null;
  business: PublicInfo; cancelMinHours: number; bookingKey: string | null;
  // GH #578 — ventana efectiva (oferta o, si 0, cancel_min_hours) + tope de
  // cambios de horario por reserva (limit null = ilimitado).
  effectiveWindowHours?: number;
  rescheduleLimit?: number | null;
  rescheduleCount?: number;
};
// V89 — estado de la entrada propia en la sala de espera.
type WaitlistInfo = {
  status: 'WAITING' | 'OFFERED' | 'CONVERTED' | 'EXPIRED' | 'DECLINED' | 'REMOVED';
  customerName: string; startsAt: string; durationMinutes: number;
  offeringName: string; offeringColor: string | null; resourceTypeName: string | null;
  business: PublicInfo; offerExpiresAt: string | null; position: number | null;
  bookingKey: string | null;
  // V93 — lugar apartado por la oferta activa ("Reformer 3").
  spotName?: string | null;
};

const inputCls = 'w-full rounded-xl border border-slate-300 px-3 py-2 text-sm';
const btnGhost = 'rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

export function isPublicBookingRoute(path: string): boolean {
  return path.startsWith('/reservar/') || path.startsWith('/reserva/') || path.startsWith('/espera/');
}

async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.message ?? 'No disponible.');
  return body as T;
}
async function publicPost<T>(path: string, payload: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.message ?? 'No se pudo procesar la solicitud.');
  return body as T;
}

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });

// --- Fase 5 (UX v2) — helpers de fecha para el carrusel/calendario -----------
const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS);
const mondayOf = (d: Date) => addDays(startOfDay(d), -((d.getDay() + 6) % 7));
const dayKeyOf = (iso: string | Date) => startOfDay(new Date(iso)).getTime();
const WEEKDAYS_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export function PublicBookingRouter() {
  const path = window.location.pathname;
  if (path.startsWith('/reservar/')) {
    const key = decodeURIComponent(path.slice('/reservar/'.length)).replace(/\/+$/, '');
    return <PublicBookPage bookingKey={key} />;
  }
  if (path.startsWith('/espera/')) {
    const token = decodeURIComponent(path.slice('/espera/'.length)).replace(/\/+$/, '');
    return <PublicWaitlistPage token={token} />;
  }
  const token = decodeURIComponent(path.slice('/reserva/'.length)).replace(/\/+$/, '');
  return <PublicManagePage token={token} />;
}

function Frame({ info, children }: { info: PublicInfo | null; children: ReactNode }) {
  const accent = info?.primaryColor || '#0F172A';
  return (
    <div className="min-h-screen bg-[#FAFAF7] font-sans text-slate-800 antialiased">
      <div className="h-1.5 w-full" style={{ background: accent }} />
      <div className="mx-auto max-w-lg px-4 py-8">
        {info && (
          <div className="mb-6 flex items-center gap-3">
            {info.logoUrl && <img src={info.logoUrl} alt="" className="h-10 w-10 rounded-xl object-contain" />}
            <div>
              <h1 className="text-lg font-semibold text-slate-900">{info.name}</h1>
              <p className="text-xs text-slate-500">Reservas en línea</p>
            </div>
          </div>
        )}
        {children}
        <p className="mt-10 text-center text-[11px] text-slate-400">Con tecnología de FluidSol</p>
      </div>
    </div>
  );
}

/**
 * Epic Pagos — bloque pay-to-reserve tras confirmar la reserva. Muestra el
 * precio y, si el offering tiene política de no-show, la EXPONE claramente
 * (% + ventana) y EXIGE aceptación explícita del cliente antes de habilitar
 * el pago (request de Arturo; flujo dispute-prone). Redirige al Checkout
 * hosted de Stripe (en la cuenta conectada del tenant).
 */
function PayToReserveBlock({ token, slot }: { token: string; slot: PublicSlot }) {
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // V105 — la reserva puede estar CUBIERTA por membresía/paquete (no se cobra);
  // se descubre por el estado de pago (covered_by_membership + saldo).
  const [cover, setCover] = useState<{ coveredByMembership: boolean; classesRemaining: number | null } | null>(null);
  useEffect(() => {
    fetch(`${API_BASE}/public/pago/reserva/${token}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => setCover(s ? { coveredByMembership: !!s.coveredByMembership, classesRemaining: s.classesRemaining ?? null } : null))
      .catch(() => setCover(null));
  }, [token]);
  // V102 — fees separados: late fee (retenido del refund al cancelar tarde)
  // y no-show fee (cargo si no llega). Cualquiera > 0 exige aceptación.
  const noShowPct = slot.noShowChargePct ?? 0;
  const latePct = slot.lateCancelChargePct ?? 0;
  const hasPolicy = noShowPct > 0 || latePct > 0;
  const price = slot.price ?? 0;
  const currency = slot.currency ?? 'MXN';

  // Cubierta por membresía/paquete: reservó con su crédito, sin pago.
  if (cover?.coveredByMembership) {
    return (
      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <p className="font-semibold">Reservaste con tu {cover.classesRemaining != null ? 'paquete' : 'membresía'} ✓</p>
        <p className="mt-1 text-[13px]">
          {cover.classesRemaining != null
            ? <>No necesitas pagar. Te quedan <b>{cover.classesRemaining}</b> clase{cover.classesRemaining === 1 ? '' : 's'} en tu paquete.</>
            : 'No necesitas pagar: tu membresía cubre esta reserva.'}
        </p>
      </div>
    );
  }

  const pay = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await publicPost<{ status: string; url?: string }>(
        `/public/pago/reserva/${token}/checkout`,
        { policyAccepted: accepted },
      );
      if (r.status === 'redirect' && r.url) { window.location.href = r.url; return; }
      if (r.status === 'paid') { window.location.href = `/reserva/${token}`; return; }
      setErr('No se pudo iniciar el pago. Intenta desde "Ver mi reserva".');
    } catch (e: any) {
      setErr(e?.message ?? 'No se pudo iniciar el pago.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">Total a pagar</span>
        <span className="text-lg font-semibold text-slate-900">
          ${price.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {currency}
        </span>
      </div>
      {hasPolicy && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-semibold">Política de cancelación</p>
          <p className="mt-1">
            {cancellationPolicyText({
              windowHours: slot.lateCancelWindowHours ?? 0,
              latePct, noShowPct, price,
            })}
          </p>
          <label className="mt-2 flex items-start gap-2">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" />
            <span>Acepto la política de cancelación y las retenciones descritas.</span>
          </label>
        </div>
      )}
      {err && <p className="mt-2 text-xs text-rose-600">{err}</p>}
      <button onClick={pay} disabled={busy || (hasPolicy && !accepted)}
        className="mt-3 w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50">
        {busy ? 'Redirigiendo…' : 'Pagar ahora'}
      </button>
      <p className="mt-2 text-center text-[11px] text-slate-400">Pago seguro procesado por Stripe.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// /reservar/:key — agenda pública + crear reserva
// ---------------------------------------------------------------------------
function PublicBookPage({ bookingKey }: { bookingKey: string }) {
  const [info, setInfo] = useState<PublicInfo | null>(null);
  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState<PublicSlot | null>(null);
  const [done, setDone] = useState<
    { kind: 'booked'; manageToken: string; slot?: PublicSlot | null } | { kind: 'waitlist'; position: number } | null
  >(null);
  // Fase 5 (UX v2): vista lista/calendario + carrusel de días + Hoy/Mostrar todo.
  const [view, setView] = useState<'lista' | 'calendario'>('lista');
  const [dayFilter, setDayFilter] = useState<number | null>(null); // dayKey del carrusel
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
  // V74 — dropdowns Instructores / Clases (derivados de los slots) + bio expandible.
  const [instructorFilter, setInstructorFilter] = useState('');
  const [offeringFilter, setOfferingFilter] = useState('');
  const [bioOpen, setBioOpen] = useState<string | null>(null); // slotId con bio abierta
  // C3 (#285) — sucursal elegida por el cliente ('' = todas; con 1 sede no se muestra).
  const [branchId, setBranchId] = useState('');

  useEffect(() => {
    publicGet<PublicInfo>(`/public/booking/${bookingKey}/info`)
      .then(setInfo)
      .catch(() => setNotFound(true));
  }, [bookingKey]);
  useEffect(() => {
    // Horizonte completo permitido por el backend (clamp server-side a 90 días).
    const to = new Date(Date.now() + 90 * DAY_MS).toISOString();
    const qs = new URLSearchParams({ to });
    if (branchId) qs.set('branchId', branchId); // C3 — filtra la oferta por sede.
    publicGet<PublicSlot[]>(`/public/booking/${bookingKey}/slots?${qs.toString()}`)
      .then(setSlots)
      .catch(() => setNotFound(true));
  }, [bookingKey, branchId]);

  const slotsByDayKey = useMemo(() => {
    const m = new Map<number, PublicSlot[]>();
    for (const s of slots) {
      const k = dayKeyOf(s.startsAt);
      (m.get(k) ?? m.set(k, []).get(k)!).push(s);
    }
    return m;
  }, [slots]);

  const instructorNames = useMemo(
    () => [...new Set(slots.map((s) => s.instructorName).filter(Boolean))] as string[],
    [slots],
  );
  const offeringNames = useMemo(
    () => [...new Set(slots.map((s) => s.offeringName))],
    [slots],
  );
  // #285 A — vertical-aware: Boutique (todo SLOT) dice "servicios"/"quién te
  // atiende"; Wellness dice "clases"/"instructores". Se deriva de los slots.
  const L = useMemo(() => bookingLabels(verticalFromKinds(slots.map((s) => s.offeringKind))), [slots]);

  const visible = useMemo(
    () => slots.filter((s) =>
      (dayFilter === null || dayKeyOf(s.startsAt) === dayFilter)
      && (!instructorFilter || s.instructorName === instructorFilter)
      && (!offeringFilter || s.offeringName === offeringFilter)),
    [slots, dayFilter, instructorFilter, offeringFilter],
  );
  const hasFilters = dayFilter !== null || !!instructorFilter || !!offeringFilter;
  const clearFilters = () => { setDayFilter(null); setInstructorFilter(''); setOfferingFilter(''); };

  const byDay = useMemo(() => {
    const m = new Map<string, PublicSlot[]>();
    for (const s of visible) {
      const k = new Date(s.startsAt).toDateString();
      (m.get(k) ?? m.set(k, []).get(k)!).push(s);
    }
    return [...m.entries()];
  }, [visible]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const goToday = () => {
    setWeekStart(mondayOf(new Date()));
    setDayFilter(startOfDay(new Date()).getTime());
  };

  if (notFound) {
    return (
      <Frame info={null}>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-sm font-medium text-slate-700">Esta página de reservas no está disponible.</p>
          <p className="mt-1 text-xs text-slate-400">Verifica el enlace con el negocio.</p>
        </div>
      </Frame>
    );
  }

  if (done?.kind === 'booked') {
    const manageUrl = `${window.location.origin}/reserva/${done.manageToken}`;
    const mode = done.slot?.paymentMode ?? 'none';
    const needsPay = mode !== 'none' && (done.slot?.price ?? 0) > 0;
    return (
      <Frame info={info}>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <p className="text-sm font-semibold text-emerald-800">
            {/* V105 — con `required` puede ser "completa tu pago" O "cubierta por
                membresía"; el bloque de abajo lo resuelve. Header neutro. */}
            {needsPay && mode === 'required' ? '✓ ¡Reserva creada!' : '✓ ¡Reserva confirmada!'}
          </p>
          <p className="mt-2 text-sm text-emerald-900">
            Guarda este enlace — es tu comprobante y la única forma de <b>modificar o cancelar</b> tu reserva:
          </p>
          <div className="mt-3 flex items-center gap-2">
            <input readOnly className="w-full rounded-lg border border-emerald-200 bg-white px-2 py-1.5 text-[11px] text-slate-600" value={manageUrl} onFocus={(e) => e.target.select()} />
            <button className={btnGhost} onClick={() => navigator.clipboard?.writeText(manageUrl)}>Copiar</button>
          </div>
          {needsPay
            ? <PayToReserveBlock token={done.manageToken} slot={done.slot!} />
            : <a href={manageUrl} className="mt-4 inline-block text-sm font-medium text-emerald-800 underline">Ver mi reserva →</a>}
        </div>
      </Frame>
    );
  }

  // V89 — ingreso a la sala de espera confirmado.
  if (done?.kind === 'waitlist') {
    return (
      <Frame info={info}>
        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-6">
          <p className="text-sm font-semibold text-sky-800">✓ Estás en la lista de espera</p>
          <p className="mt-2 text-sm text-sky-900">
            Tu posición: <b>#{done.position}</b>. Si se libera un lugar te avisaremos <b>por correo</b> con
            un enlace para reclamarlo (tendrás un tiempo límite para confirmar).
          </p>
          <p className="mt-2 text-xs text-sky-700">No necesitas hacer nada más. Revisa tu bandeja (y spam) cuando llegue el aviso.</p>
        </div>
      </Frame>
    );
  }

  const slotCard = (s: PublicSlot) => {
    const full = s.freeCount <= 0;
    // V89 — clase llena con sala de espera: la tarjeta sigue clickeable.
    const canWaitlist = full && info?.waitlistEnabled === true;
    const typed = (s.resourceTypes?.length ?? 0) > 0;
    return (
      <div key={s.slotId} className={`rounded-2xl border bg-white ${full ? (canWaitlist ? 'border-amber-200' : 'border-slate-100') : 'border-slate-200'}`}>
        <button disabled={full && !canWaitlist} onClick={() => setSelected(s)}
          className={`flex w-full items-center justify-between px-4 py-3 text-left ${full && !canWaitlist ? 'opacity-50' : 'hover:opacity-80'}`}>
          <span className="flex items-center gap-2">
            {s.offeringColor && <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.offeringColor }} />}
            <span>
              <span className="block text-sm font-medium text-slate-800">{s.offeringName}</span>
              <span className="block text-xs text-slate-400">
                {fmtTime(s.startsAt)} · {s.durationMinutes} min{s.instructorName ? ` · ${L.withProvider(s.instructorName)}` : ''}
              </span>
              {/* V88 — disponibilidad por tipo de espacio */}
              {typed && (
                <span className="mt-0.5 block text-[11px] text-slate-500">
                  {s.resourceTypes!.map((t) => `${t.name}: ${t.free > 0 ? `${t.free} lugar${t.free === 1 ? '' : 'es'}` : 'lleno'}`).join(' · ')}
                </span>
              )}
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-0.5">
            <span className={`text-xs font-medium ${full ? (canWaitlist ? 'text-amber-600' : 'text-slate-400') : 'text-emerald-600'}`}>
              {full
                ? (canWaitlist ? 'Lleno · Lista de espera' : 'Lleno')
                : `${s.freeCount} lugar${s.freeCount === 1 ? '' : 'es'}`}
            </span>
            {/* Epic Pagos: precio si la clase cobra. */}
            {(s.paymentMode ?? 'none') !== 'none' && (s.price ?? 0) > 0 && (
              <span className="text-[11px] font-semibold text-slate-700">
                ${(s.price ?? 0).toLocaleString('es-MX')}
              </span>
            )}
          </span>
        </button>
        {/* V74 — biografía del instructor (expandible) */}
        {s.instructorBio && (
          <div className="border-t border-slate-100 px-4 py-1.5">
            <button className="text-[11px] font-medium text-slate-400 hover:text-slate-600"
                    onClick={() => setBioOpen(bioOpen === s.slotId ? null : s.slotId)}>
              {bioOpen === s.slotId ? 'Ocultar biografía' : 'Mostrar biografía'}
            </button>
            {bioOpen === s.slotId && (
              <p className="pb-1.5 text-xs text-slate-500">{s.instructorBio}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Frame info={info}>
      {selected
        ? <PublicBookingForm bookingKey={bookingKey} slot={selected}
                             waitlistEnabled={info?.waitlistEnabled === true}
                             avisoUrl={info?.avisoUrl ?? null}
                             onBack={() => setSelected(null)} onDone={setDone} />
        : (
          <div className="space-y-4">
            {/* UX v2: toggle de vista + Hoy + Mostrar todo */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
                <button onClick={() => setView('lista')}
                  className={`rounded-lg px-3 py-1 text-xs font-medium ${view === 'lista' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                  Lista
                </button>
                <button onClick={() => setView('calendario')}
                  className={`rounded-lg px-3 py-1 text-xs font-medium ${view === 'calendario' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                  Calendario
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button className={btnGhost} onClick={goToday}>Hoy</button>
                {hasFilters && (
                  <button className={btnGhost} onClick={clearFilters}>Mostrar todo</button>
                )}
              </div>
            </div>

            {/* C3 (#285) — selector de SUCURSAL primero (solo con 2+ sedes; con 1 no
                se muestra y la oferta es la de siempre). Al elegir, refetch por sede. */}
            {(info?.branches?.length ?? 0) > 1 && (
              <div className="mb-2">
                <label className="block text-[11px] font-medium uppercase tracking-wide text-slate-400">Sucursal</label>
                <select className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-800"
                        value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">Todas las sucursales</option>
                  {info!.branches!.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            {/* V74 — dropdowns Clases / Instructores */}
            {(offeringNames.length > 1 || instructorNames.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {offeringNames.length > 1 && (
                  <select className="flex-1 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700"
                          value={offeringFilter} onChange={(e) => setOfferingFilter(e.target.value)}>
                    <option value="">{L.allOfferings}</option>
                    {offeringNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                )}
                {instructorNames.length > 0 && (
                  <select className="flex-1 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700"
                          value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)}>
                    <option value="">{L.allProviders}</option>
                    {instructorNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                )}
              </div>
            )}

            {/* UX v2: carrusel semanal de días */}
            <div className="flex items-center gap-1.5">
              <button className={btnGhost} aria-label="Semana anterior"
                      onClick={() => setWeekStart(addDays(weekStart, -7))}>◀</button>
              <div className="grid flex-1 grid-cols-7 gap-1">
                {weekDays.map((d, i) => {
                  const k = d.getTime();
                  const count = (slotsByDayKey.get(k) ?? []).length;
                  const active = dayFilter === k;
                  const isToday = startOfDay(new Date()).getTime() === k;
                  return (
                    <button key={k} disabled={count === 0}
                      onClick={() => setDayFilter(active ? null : k)}
                      className={`rounded-xl border px-1 py-1.5 text-center ${active ? 'border-slate-900 bg-slate-900 text-white' : count === 0 ? 'border-slate-100 bg-white text-slate-300' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}>
                      <span className="block text-[10px] font-medium uppercase">{WEEKDAYS_SHORT[i]}</span>
                      <span className={`block text-sm font-semibold ${isToday && !active ? 'underline' : ''}`}>{d.getDate()}</span>
                      <span className={`block text-[9px] ${active ? 'text-slate-300' : 'text-slate-400'}`}>{count > 0 ? count : '·'}</span>
                    </button>
                  );
                })}
              </div>
              <button className={btnGhost} aria-label="Semana siguiente"
                      onClick={() => setWeekStart(addDays(weekStart, 7))}>▶</button>
            </div>

            {byDay.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                {hasFilters
                  ? <>No hay horarios con esos filtros. <button className="font-medium underline" onClick={clearFilters}>Mostrar todo</button></>
                  : 'Por ahora no hay horarios disponibles. Vuelve pronto.'}
              </div>
            )}

            {view === 'lista' ? (
              <div className="space-y-5">
                {byDay.map(([day, list]) => (
                  <div key={day}>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">{fmtDay(list[0].startsAt)}</p>
                    <div className="space-y-2">{list.map(slotCard)}</div>
                  </div>
                ))}
              </div>
            ) : (
              /* Vista calendario: la semana del carrusel, 7 columnas */
              <div className="grid grid-cols-7 gap-1">
                {weekDays.map((d) => {
                  const list = (slotsByDayKey.get(d.getTime()) ?? [])
                    .filter((s) => dayFilter === null || dayKeyOf(s.startsAt) === dayFilter);
                  return (
                    <div key={d.getTime()} className="min-h-[120px] rounded-xl border border-slate-200 bg-white p-1">
                      <p className="mb-1 text-center text-[10px] font-medium text-slate-400">{d.getDate()}</p>
                      <div className="space-y-1">
                        {list.map((s) => {
                          const full = s.freeCount <= 0;
                          return (
                            <button key={s.slotId} disabled={full} onClick={() => setSelected(s)}
                              title={`${s.offeringName} · ${fmtTime(s.startsAt)}${full ? ' · lleno' : ` · ${s.freeCount} libres`}`}
                              className={`block w-full truncate rounded-lg border px-1 py-0.5 text-left text-[10px] ${full ? 'border-slate-100 text-slate-300' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`}>
                              <span className="tabular-nums">{fmtTime(s.startsAt)}</span> {s.offeringName}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
    </Frame>
  );
}

function PublicBookingForm({ bookingKey, slot, waitlistEnabled, avisoUrl = null, onBack, onDone }: {
  bookingKey: string; slot: PublicSlot; waitlistEnabled: boolean; avisoUrl?: string | null; onBack: () => void;
  onDone: (r: { kind: 'booked'; manageToken: string; slot?: PublicSlot | null } | { kind: 'waitlist'; position: number }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — oculto para humanos
  // V172 — consentimiento de privacidad (misma maquinaria que la tienda): casilla NO
  // premarcada; sin ella el submit queda deshabilitado y el server igual rechaza (400).
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  // V75 — preguntas configuradas por el negocio (requeridas también se
  // validan server-side dentro de la fn DEFINER).
  const [fields, setFields] = useState<PublicFormField[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // V88 — tipo de espacio elegido (obligatorio si la clase es tipada).
  const types = slot.resourceTypes ?? [];
  const typed = types.length > 0;
  const [typeId, setTypeId] = useState(() => {
    // Preselección amable: el primer tipo con lugar; si todo está lleno, el primero.
    const free = types.find((t) => t.free > 0);
    return (free ?? types[0])?.id ?? '';
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // V93 — plano del aula elegida (si tiene mapa) + lugar seleccionado.
  const [spotRooms, setSpotRooms] = useState<PublicSpotRoom[]>([]);
  const [spotId, setSpotId] = useState('');
  const [spotsVersion, setSpotsVersion] = useState(0); // re-fetch tras "lugar tomado"

  const selectedType = typed ? types.find((t) => t.id === typeId) ?? null : null;
  // V89 — modo lista de espera: la clase (o el tipo elegido) está llena.
  const wantsWaitlist = typed ? (selectedType ? selectedType.free <= 0 : false) : slot.freeCount <= 0;
  const waitlistMode = wantsWaitlist && waitlistEnabled;
  const blockedFull = wantsWaitlist && !waitlistEnabled;
  // V93 — aula con mapa: el lugar es obligatorio (no aplica al unirse a la lista:
  // el lugar se asigna al ofertar).
  const needsSpot = selectedType?.hasMap === true && !waitlistMode && !blockedFull;
  const room = spotRooms.find((r) => r.roomId === typeId) ?? null;

  useEffect(() => {
    publicGet<PublicFormField[]>(`/public/booking/${bookingKey}/form`)
      .then(setFields)
      .catch(() => setFields([]));
  }, [bookingKey]);

  useEffect(() => {
    if (!types.some((t) => t.hasMap)) return;
    publicGet<PublicSpotRoom[]>(`/public/booking/${bookingKey}/slots/${slot.slotId}/spots`)
      .then(setSpotRooms)
      .catch(() => setSpotRooms([]));
  }, [bookingKey, slot.slotId, spotsVersion]);
  useEffect(() => { setSpotId(''); }, [typeId]);

  const setAnswer = (fieldId: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  const missingRequired = !waitlistMode
    && fields.some((f) => f.isRequired && !(answers[f.fieldId] ?? '').trim());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (waitlistMode) {
        const r = await publicPost<{ ok: true; position: number }>(
          `/public/booking/${bookingKey}/waitlist`,
          {
            slotId: slot.slotId, name: name.trim(), email: email.trim(),
            ...(phone.trim() && { phone: phone.trim() }),
            ...(typed && typeId && { resourceTypeId: typeId }),
            acceptedPrivacy, // V172
            ...(website && { website }),
          },
        );
        onDone({ kind: 'waitlist', position: r.position });
        return;
      }
      const filled = Object.fromEntries(
        Object.entries(answers).filter(([, v]) => v.trim() !== ''));
      const r = await publicPost<{ ok: true; manageToken: string }>(
        `/public/booking/${bookingKey}/bookings`,
        {
          slotId: slot.slotId, name: name.trim(), email: email.trim(),
          ...(phone.trim() && { phone: phone.trim() }),
          ...(typed && typeId && { resourceTypeId: typeId }),
          ...(needsSpot && spotId && { spotId }),
          ...(Object.keys(filled).length > 0 && { answers: filled }),
          acceptedPrivacy, // V172
          ...(website && { website }),
        },
      );
      onDone({ kind: 'booked', manageToken: r.manageToken, slot });
    } catch (e: any) {
      setErr(e?.message ?? 'No se pudo procesar la solicitud.');
      // V93 — carrera de lugar (alguien lo ganó): refresca el plano y re-elige.
      if (needsSpot) { setSpotId(''); setSpotsVersion((v) => v + 1); }
      setBusy(false);
    }
  };

  const fieldInput = (f: PublicFormField) => {
    const v = answers[f.fieldId] ?? '';
    switch (f.fieldType) {
      case 'select':
        return (
          <select className={inputCls} value={v} onChange={(e) => setAnswer(f.fieldId, e.target.value)} required={f.isRequired}>
            <option value="">{f.label}{f.isRequired ? ' *' : ''}</option>
            {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'date':
        return (
          <label className="block text-[12px] text-slate-500">{f.label}{f.isRequired ? ' *' : ''}
            <input type="date" className={inputCls} value={v} onChange={(e) => setAnswer(f.fieldId, e.target.value)} required={f.isRequired} />
          </label>
        );
      case 'boolean':
        return (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={v === 'true'} onChange={(e) => setAnswer(f.fieldId, e.target.checked ? 'true' : 'false')} />
            {f.label}{f.isRequired ? ' *' : ''}
          </label>
        );
      default:
        return (
          <input className={inputCls} placeholder={`${f.label}${f.isRequired ? ' *' : ''}`} value={v}
                 onChange={(e) => setAnswer(f.fieldId, e.target.value)} required={f.isRequired} maxLength={500} />
        );
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-5">
      <button type="button" className="mb-3 text-xs text-slate-400 hover:text-slate-600" onClick={onBack}>← Elegir otro horario</button>
      <p className="text-sm font-semibold text-slate-800">{slot.offeringName}</p>
      <p className="text-xs text-slate-500">
        {fmtDay(slot.startsAt)} · {fmtTime(slot.startsAt)} · {slot.durationMinutes} min
        {slot.instructorName ? ` · ${slot.instructorName}` : ''}
      </p>

      {/* V88 — selector de tipo de espacio (obligatorio en clases tipadas) */}
      {typed && (
        <div className="mt-3">
          <p className="text-[12px] font-medium text-slate-500">Elige tu tipo de espacio</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {types.map((t) => {
              const tFull = t.free <= 0;
              const selectable = !tFull || waitlistEnabled;
              return (
                <button key={t.id} type="button" disabled={!selectable}
                  onClick={() => setTypeId(t.id)}
                  className={`rounded-xl border px-3 py-1.5 text-xs font-medium disabled:opacity-40 ${
                    typeId === t.id
                      ? (tFull ? 'border-amber-500 bg-amber-500 text-white' : 'border-slate-900 bg-slate-900 text-white')
                      : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'}`}>
                  {t.name} · {tFull ? 'lleno' : `${t.free} lugar${t.free === 1 ? '' : 'es'}`}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* V93 — picker visual de lugar (el aula elegida tiene mapa) */}
      {needsSpot && room && (
        <div className="mt-3">
          <p className="text-[12px] font-medium text-slate-500">
            Elige tu lugar{room.freeCount <= 0 ? '' : ` (${room.roomName})`}
          </p>
          {room.freeCount <= 0 && (
            <p className="mt-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              El aula ya no tiene cupo disponible.
            </p>
          )}
          <div className="mt-1.5">
            <SpotMapPicker spots={room.spots} layout={room.layout ?? null}
                           value={spotId} onChange={setSpotId}
                           disabledAll={room.freeCount <= 0} />
          </div>
          <p className="mt-1 text-[10px] text-slate-400">🔒 = ocupado o apartado para la lista de espera.</p>
        </div>
      )}

      {/* V89 — modo lista de espera (la clase o el tipo elegido está lleno) */}
      {waitlistMode && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {typed ? <><b>{selectedType?.name}</b> está lleno.</> : <>Esta clase está <b>llena</b>.</>}{' '}
          Únete a la lista de espera: si se libera un lugar te avisaremos por correo con un enlace para reclamarlo.
        </p>
      )}
      {blockedFull && (
        <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          {typed ? 'Ese tipo de espacio está lleno.' : 'Esta clase está llena.'} Elige otra opción u otro horario.
        </p>
      )}

      <div className="mt-4 space-y-3">
        <input className={inputCls} placeholder="Tu nombre *" value={name} onChange={(e) => setName(e.target.value)} required maxLength={150} />
        <input className={inputCls} placeholder="Tu email *" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} />
        <input className={inputCls} placeholder="Teléfono (opcional)" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={50} />
        {!waitlistMode && fields.map((f) => <div key={f.fieldId}>{fieldInput(f)}</div>)}
        {/* Honeypot: invisible para humanos, los bots lo llenan. */}
        <input className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true"
               name="website" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      {/* V172 — consentimiento en el punto de recolección (aplica también a la lista de
          espera: recaba los mismos datos). Casilla NO premarcada + enlace al aviso. */}
      <label className="mt-3 flex items-start gap-2 text-[12px] text-slate-600">
        <input type="checkbox" className="mt-0.5" checked={acceptedPrivacy}
               onChange={(e) => setAcceptedPrivacy(e.target.checked)} />
        <span>
          Acepto el{' '}
          {avisoUrl ? (
            <a href={avisoUrl} target="_blank" rel="noreferrer" className="font-medium underline">aviso de privacidad</a>
          ) : (
            <span className="font-medium">aviso de privacidad</span>
          )}{' '}del negocio para el uso de mis datos (nombre, correo, teléfono) en esta {waitlistMode ? 'lista de espera' : 'reserva'}. *
        </span>
      </label>
      {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
      <button type="submit"
        disabled={busy || !name.trim() || !email.trim() || !acceptedPrivacy || missingRequired || blockedFull || (typed && !typeId) || (needsSpot && !spotId)}
        className={`mt-4 w-full rounded-xl py-2.5 text-sm font-medium text-white disabled:opacity-50 ${waitlistMode ? 'bg-amber-600 hover:bg-amber-500' : 'bg-slate-900 hover:bg-slate-700'}`}>
        {busy
          ? (waitlistMode ? 'Anotándote…' : 'Reservando…')
          : (waitlistMode ? 'Unirme a la lista de espera' : 'Confirmar reserva')}
      </button>
      <p className="mt-2 text-center text-[11px] text-slate-400">
        {waitlistMode
          ? 'Sin costo por anotarte. El pago se realiza en el establecimiento.'
          : 'Recibirás un enlace para modificar o cancelar. El pago se realiza en el establecimiento.'}
      </p>
      {/* V92 — leyenda de consentimiento de comunicados (LFPDPPP): la inscripción
          implica aceptar recibirlos; cada correo lleva link de baja. */}
      <p className="mt-1 text-center text-[11px] text-slate-400">
        Al inscribirte aceptas recibir comunicados de este negocio; puedes darte de
        baja en cualquier momento desde cualquiera de sus correos.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// /espera/:token — oferta de la sala de espera (V89): reclamar o rechazar.
// ---------------------------------------------------------------------------
const WAITLIST_COPY: Record<WaitlistInfo['status'], { label: string; cls: string }> = {
  WAITING:   { label: 'En espera',  cls: 'bg-sky-100 text-sky-700' },
  OFFERED:   { label: '¡Lugar disponible!', cls: 'bg-amber-100 text-amber-700' },
  CONVERTED: { label: 'Reservado',  cls: 'bg-emerald-100 text-emerald-700' },
  EXPIRED:   { label: 'Oferta vencida', cls: 'bg-slate-100 text-slate-500' },
  DECLINED:  { label: 'Rechazada',  cls: 'bg-slate-100 text-slate-500' },
  REMOVED:   { label: 'Retirada',   cls: 'bg-slate-100 text-slate-500' },
};

function PublicWaitlistPage({ token }: { token: string }) {
  const [data, setData] = useState<WaitlistInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [claimed, setClaimed] = useState<{ manageToken: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    publicGet<WaitlistInfo>(`/public/booking/waitlist/${token}`)
      .then((d) => { setData(d); setErr(null); })
      .catch(() => setNotFound(true));
  useEffect(() => { load(); }, [token]);

  const claim = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await publicPost<{ ok: true; manageToken: string }>(
        `/public/booking/waitlist/${token}/claim`, {});
      setClaimed({ manageToken: r.manageToken });
    } catch (e: any) { setErr(e?.message ?? 'No se pudo reclamar.'); await load(); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    if (!window.confirm('¿Rechazar el lugar? Pasará a la siguiente persona de la lista.')) return;
    setBusy(true); setErr(null);
    try { await publicPost(`/public/booking/waitlist/${token}/decline`, {}); await load(); }
    catch (e: any) { setErr(e?.message ?? 'No se pudo procesar.'); }
    finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <Frame info={null}>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-sm font-medium text-slate-700">No encontramos esta lista de espera.</p>
          <p className="mt-1 text-xs text-slate-400">El enlace puede ser incorrecto o haber expirado.</p>
        </div>
      </Frame>
    );
  }
  if (!data) return <Frame info={null}><p className="text-center text-sm text-slate-400">Cargando…</p></Frame>;

  if (claimed) {
    const manageUrl = `${window.location.origin}/reserva/${claimed.manageToken}`;
    return (
      <Frame info={data.business}>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <p className="text-sm font-semibold text-emerald-800">✓ ¡Tu lugar quedó reservado!</p>
          <p className="mt-2 text-sm text-emerald-900">
            Guarda este enlace — es tu comprobante y la única forma de <b>modificar o cancelar</b> tu reserva:
          </p>
          <div className="mt-3 flex items-center gap-2">
            <input readOnly className="w-full rounded-lg border border-emerald-200 bg-white px-2 py-1.5 text-[11px] text-slate-600" value={manageUrl} onFocus={(e) => e.target.select()} />
            <button className={btnGhost} onClick={() => navigator.clipboard?.writeText(manageUrl)}>Copiar</button>
          </div>
          <p className="mt-3 text-xs text-emerald-800">El pago se realiza directamente en el establecimiento.</p>
          <a href={manageUrl} className="mt-2 inline-block text-sm font-medium text-emerald-800 underline">Ver mi reserva →</a>
        </div>
      </Frame>
    );
  }

  const st = WAITLIST_COPY[data.status];
  const offerActive = data.status === 'OFFERED' && data.offerExpiresAt
    && new Date(data.offerExpiresAt).getTime() > Date.now();
  return (
    <Frame info={data.business}>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-slate-800">{data.offeringName}</p>
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {fmtDay(data.startsAt)} · {fmtTime(data.startsAt)} · {data.durationMinutes} min
          {data.resourceTypeName ? ` · ${data.resourceTypeName}` : ''}
          {data.spotName ? <> · Lugar: <b>{data.spotName}</b></> : null}
        </p>
        <p className="text-xs text-slate-400">A nombre de {data.customerName}</p>
        {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}

        {offerActive && (
          <div className="mt-4 space-y-3">
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              ¡Se liberó {data.spotName ? <>el lugar <b>{data.spotName}</b></> : 'un lugar'} para ti!
              Queda apartado mientras dure la oferta: tienes hasta el <b>{fmtDay(data.offerExpiresAt!)} · {fmtTime(data.offerExpiresAt!)}</b> para
              reclamarlo. Si no lo reclamas, pasará a la siguiente persona.
            </p>
            <button onClick={claim} disabled={busy}
              className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50">
              {busy ? 'Reservando…' : 'Reclamar mi lugar'}
            </button>
            <button onClick={decline} disabled={busy}
              className="w-full rounded-xl border border-slate-300 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              No puedo asistir — ceder mi lugar
            </button>
            <p className="text-center text-[11px] text-slate-400">Sin pago en línea: pagas al llegar al establecimiento.</p>
          </div>
        )}
        {data.status === 'WAITING' && (
          <p className="mt-4 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
            Sigues en la lista{data.position ? <> (posición <b>#{data.position}</b>)</> : null}. Si se libera un
            lugar te avisaremos por correo. {' '}
            <button className="font-medium underline" onClick={decline} disabled={busy}>Salir de la lista</button>
          </p>
        )}
        {data.status === 'OFFERED' && !offerActive && (
          <p className="mt-4 text-sm text-slate-500">Esta oferta está por procesarse o acaba de vencer. Vuelve a cargar la página.</p>
        )}
        {data.status === 'CONVERTED' && (
          <p className="mt-4 text-sm text-emerald-700">Ya reclamaste este lugar. Revisa tu correo: ahí está tu confirmación y el enlace para gestionar tu reserva.</p>
        )}
        {(data.status === 'EXPIRED' || data.status === 'DECLINED' || data.status === 'REMOVED') && (
          <div className="mt-4 space-y-2">
            <p className="text-sm text-slate-500">
              {data.status === 'EXPIRED' && 'La oferta venció y el lugar pasó a la siguiente persona.'}
              {data.status === 'DECLINED' && 'Cediste tu lugar; pasó a la siguiente persona.'}
              {data.status === 'REMOVED' && 'Esta entrada ya no está activa.'}
            </p>
            {data.bookingKey && (
              <a href={`${window.location.origin}/reservar/${data.bookingKey}`}
                 className="inline-block text-sm font-medium text-slate-700 underline">
                Ver otros horarios disponibles →
              </a>
            )}
          </div>
        )}
      </div>
    </Frame>
  );
}

// ---------------------------------------------------------------------------
// /reserva/:token — gestionar la reserva propia
// ---------------------------------------------------------------------------
const MANAGE_STATUS: Record<ManageInfo['status'], { label: string; cls: string }> = {
  BOOKED:    { label: 'Confirmada', cls: 'bg-emerald-100 text-emerald-700' },
  CANCELLED: { label: 'Cancelada',  cls: 'bg-slate-100 text-slate-500' },
  ATTENDED:  { label: 'Asistida',   cls: 'bg-sky-100 text-sky-700' },
  NO_SHOW:   { label: 'No asistida', cls: 'bg-amber-100 text-amber-700' },
};

/**
 * Epic Pagos — sección de pago/factura en /reserva/:token. Muestra el pago
 * pendiente (con botón para completarlo), el pago confirmado, y — si el
 * negocio ofrece factura y hay CFDI disponible — el flujo de solicitud del
 * CFDI (emitCore camino booking) con captura fiscal + estado del folio.
 */
function ManagePaymentSection({ token, status, onPendingPay }: {
  token: string; status: string;
  /** Epic Pagos — avisa al padre si la reserva sigue SIN pagar en modo
   *  `required` (el badge no debe decir "Confirmada" hasta el pago). */
  onPendingPay?: (pending: boolean) => void;
}) {
  type PayState = {
    paymentsEnabled: boolean; paymentStatus: string; paymentMode: string;
    price: number | null; currency: string; paidAmount: number | null;
    noShowChargePct: number; cfdiStatus: string | null; cfdiFolio: string | null;
    // PR1 (P0) — compuerta FAIL-CLOSED "facturar al reservar": el negocio la
    // ofrece solo si booking_invoice_enabled AND emisor listo AND Pagos activo.
    // Off => no se muestra "¿Necesitas factura?" (una factura ya emitida sí se ve).
    invoiceOffer?: boolean;
    // V105 — cubierta por membresía/paquete (reservó con crédito, sin pago).
    coveredByMembership?: boolean; classesRemaining?: number | null;
  };
  const [st, setSt] = useState<PayState | null>(null);
  const [invoicing, setInvoicing] = useState(false);
  const [fiscal, setFiscal] = useState({ rfc: '', razonSocial: '', regimenFiscal: '616', usoCfdi: 'G03', cp: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    fetch(`${API_BASE}/public/pago/reserva/${token}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s: PayState | null) => {
        setSt(s);
        // Cubierta por membresía NO es "pago pendiente" — el badge dice Confirmada.
        onPendingPay?.(Boolean(
          s && s.paymentsEnabled && s.paymentMode === 'required'
          && s.paymentStatus !== 'paid' && !s.coveredByMembership,
        ));
      })
      .catch(() => setSt(null));
  useEffect(() => { load(); }, [token]);

  if (!st || !st.paymentsEnabled || st.paymentMode === 'none') return null;

  // V105 — reserva cubierta por membresía/paquete: sin cobro ni factura (no
  // hubo transacción). Muestra el saldo del paquete.
  if (st.coveredByMembership) {
    return (
      <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
        <p className="font-medium">Reservaste con tu {st.classesRemaining != null ? 'paquete' : 'membresía'} ✓</p>
        {st.classesRemaining != null && (
          <p className="mt-0.5 text-[12px]">Te quedan <b>{st.classesRemaining}</b> clase{st.classesRemaining === 1 ? '' : 's'}.</p>
        )}
      </div>
    );
  }

  const payNow = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await publicPost<{ status: string; url?: string }>(
        `/public/pago/reserva/${token}/checkout`, { policyAccepted: true });
      if (r.url) window.location.href = r.url;
    } catch (e: any) { setErr(e?.message ?? 'No se pudo iniciar el pago.'); }
    finally { setBusy(false); }
  };

  const requestInvoice = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await publicPost<{ status: string; folio: string | null; error: string | null }>(
        `/public/pago/reserva/${token}/invoice`,
        { ...fiscal, rfc: fiscal.rfc.trim().toUpperCase(), cp: fiscal.cp.trim(), email: fiscal.email.trim() });
      // PR5 — sin duplicar: la línea canónica "Factura emitida · folio" la pinta
      // el estado recargado (st.cfdiStatus==='stamped'), NO un segundo mensaje verde.
      if (r.status === 'stamped') { setInvoicing(false); load(); }
      // 'pending' = timbrado en vuelo (p.ej. doble-click: otra emisión ganó el claim).
      else if (r.status === 'pending') { setInvoicing(false); setMsg('Estamos generando tu factura… te llegará por correo.'); load(); }
      else setErr(r.error ?? 'No se pudo timbrar; revisa tus datos.');
    } catch (e: any) { setErr(e?.message ?? 'No se pudo emitir la factura.'); }
    finally { setBusy(false); }
  };

  const fi = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm';

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
      {/* `required` nace payment_status='none' (el hold llega al abrir checkout):
          el CTA cubre AMBOS estados sin pagar, no solo 'pending'. */}
      {status === 'BOOKED' && st.paymentStatus !== 'paid'
        && (st.paymentStatus === 'pending' || st.paymentMode === 'required') && (
        <div>
          <p className="text-sm font-medium text-amber-800">Pago pendiente</p>
          <p className="text-xs text-slate-500">Tu lugar se libera si no completas el pago a tiempo.</p>
          {err && <p className="mt-1 text-xs text-rose-600">{err}</p>}
          <button onClick={payNow} disabled={busy}
            className="mt-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
            {busy ? '…' : 'Completar pago'}
          </button>
        </div>
      )}
      {st.paymentStatus === 'paid' && (
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-emerald-800">Pagado ✓</span>
            <span className="text-sm tabular-nums text-slate-700">
              ${Number(st.paidAmount ?? st.price ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </span>
          </div>
          {msg && <p className="mt-1 text-xs text-emerald-700">{msg}</p>}
          {st.cfdiStatus === 'stamped' ? (
            <p className="mt-2 text-xs text-slate-600">Factura emitida · folio <span className="font-mono">{st.cfdiFolio}</span></p>
          ) : !st.invoiceOffer ? (
            // PR1 (P0) — compuerta cerrada: sin oferta de factura ni formulario
            // (el backend igual rechaza el emit si lo llaman directo).
            null
          ) : !invoicing ? (
            <button onClick={() => setInvoicing(true)} className="mt-2 text-xs font-medium text-slate-700 underline">
              ¿Necesitas factura?
            </button>
          ) : (
            <div className="mt-2 space-y-1.5">
              <p className="text-[11px] text-slate-500">Captura tus datos fiscales (como en tu Constancia).</p>
              <input className={fi} placeholder="RFC" value={fiscal.rfc} onChange={(e) => setFiscal({ ...fiscal, rfc: e.target.value })} />
              <input className={fi} placeholder="Razón social" value={fiscal.razonSocial} onChange={(e) => setFiscal({ ...fiscal, razonSocial: e.target.value })} />
              <div className="flex gap-1.5">
                <input className={fi} placeholder="Régimen (ej. 616)" value={fiscal.regimenFiscal} onChange={(e) => setFiscal({ ...fiscal, regimenFiscal: e.target.value })} />
                <input className={fi} placeholder="Uso (ej. G03)" value={fiscal.usoCfdi} onChange={(e) => setFiscal({ ...fiscal, usoCfdi: e.target.value })} />
              </div>
              <div className="flex gap-1.5">
                <input className={fi} placeholder="CP fiscal" value={fiscal.cp} onChange={(e) => setFiscal({ ...fiscal, cp: e.target.value })} />
                <input className={fi} placeholder="Correo" value={fiscal.email} onChange={(e) => setFiscal({ ...fiscal, email: e.target.value })} />
              </div>
              {err && <p className="text-xs text-rose-600">{err}</p>}
              <button onClick={requestInvoice} disabled={busy}
                className="w-full rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                {busy ? 'Timbrando…' : 'Emitir factura'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PublicManagePage({ token }: { token: string }) {
  const [data, setData] = useState<ManageInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [moving, setMoving] = useState(false);
  const [options, setOptions] = useState<PublicSlot[]>([]);
  const [target, setTarget] = useState('');
  // V93 (D3) — el cliente RE-ELIGE lugar en el destino (pre-selección del
  // lugar previo si sigue libre).
  const [moveRooms, setMoveRooms] = useState<PublicSpotRoom[]>([]);
  const [moveSpotId, setMoveSpotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // Epic Pagos — true mientras la reserva `required` siga sin pagar (lo
  // reporta ManagePaymentSection): el badge dice "pago pendiente", no
  // "Confirmada" (la confirmación llega con el pago).
  const [pendingPay, setPendingPay] = useState(false);

  // PR4 — ¿la reserva tiene CFDI timbrado? (para la leyenda fiscal SUAVE al
  // cancelar). Best-effort: si no hay pago/CFDI el endpoint 404ea → false.
  const [cfdiStamped, setCfdiStamped] = useState(false);

  const load = () =>
    publicGet<ManageInfo>(`/public/booking/manage/${token}`)
      .then((d) => { setData(d); setErr(null); })
      .catch(() => setNotFound(true));
  useEffect(() => { load(); }, [token]);
  useEffect(() => {
    publicGet<{ cfdiStatus?: string | null }>(`/public/pago/reserva/${token}`)
      .then((s) => setCfdiStamped(s?.cfdiStatus === 'stamped'))
      .catch(() => setCfdiStamped(false));
  }, [token]);

  const targetSlot = options.find((s) => s.slotId === target) ?? null;
  const needsSpot =
    !!data?.resourceTypeId &&
    targetSlot?.resourceTypes?.find((t) => t.id === data.resourceTypeId)?.hasMap === true;
  const moveRoom = needsSpot ? moveRooms.find((r) => r.roomId === data!.resourceTypeId) ?? null : null;

  useEffect(() => {
    setMoveSpotId(''); setMoveRooms([]);
    if (!target || !needsSpot || !data?.bookingKey) return;
    publicGet<PublicSpotRoom[]>(`/public/booking/${data.bookingKey}/slots/${target}/spots`)
      .then((rooms) => {
        setMoveRooms(rooms);
        const room = rooms.find((r) => r.roomId === data.resourceTypeId);
        const same = room?.spots.find((s) => !s.taken && s.label === data.spotName);
        if (same) setMoveSpotId(same.id);
      })
      .catch(() => setMoveRooms([]));
  }, [target, needsSpot]);

  const cancel = async () => {
    if (!window.confirm(cfdiStamped
      ? '¿Cancelar tu reserva? Tiene factura emitida — el reembolso y la nota de crédito los gestiona el negocio con su contador.'
      : '¿Cancelar tu reserva?')) return;
    setBusy(true); setErr(null);
    try {
      // Epic Pagos — reserva pagada: reembolso según política; V105 — reserva
      // cubierta por membresía/paquete: devuelve la clase al conteo.
      const r = await publicPost<{
        ok: boolean;
        refund: { amount: number; currency: string; retainedPct: number } | null;
        creditReturned?: boolean;
      }>(`/public/booking/manage/${token}/cancel`, {});
      setMsg(r.refund
        ? `Tu reserva fue cancelada. Te devolvimos $${r.refund.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${r.refund.currency}` +
          `${r.refund.retainedPct > 0 ? ` (se retuvo el ${r.refund.retainedPct}% por cancelación tardía)` : ''} a tu método de pago.`
        : r.creditReturned
          ? 'Tu reserva fue cancelada y te devolvimos la clase a tu paquete.'
          : 'Tu reserva fue cancelada.');
      await load();
    }
    catch (e: any) { setErr(e?.message ?? 'No se pudo cancelar.'); }
    finally { setBusy(false); }
  };

  const startMove = async () => {
    if (!data?.bookingKey) return;
    setMoving(true); setErr(null);
    try {
      const rows = await publicGet<PublicSlot[]>(`/public/booking/${data.bookingKey}/slots`);
      // GH #578 — solo horarios del MISMO tipo de cita (el prepago no se mueve a
      // otra oferta con otro precio); la API lo refuerza con 409.
      setOptions(rows.filter((s) => s.freeCount > 0 && s.offeringName === data.offeringName));
    } catch { setErr('No se pudieron cargar los horarios.'); }
  };

  const move = async () => {
    if (!target) return;
    setBusy(true); setErr(null);
    try {
      await publicPost(`/public/booking/manage/${token}/reschedule`, {
        newSlotId: target,
        ...(needsSpot && moveSpotId && { spotId: moveSpotId }),
      });
      setMsg('Tu reserva fue movida.'); setMoving(false); setTarget(''); await load();
    } catch (e: any) {
      setErr(e?.message ?? 'No se pudo mover.');
      // V93 — carrera de lugar: refresca el plano del destino y re-elige.
      if (needsSpot && data?.bookingKey) {
        setMoveSpotId('');
        publicGet<PublicSpotRoom[]>(`/public/booking/${data.bookingKey}/slots/${target}/spots`)
          .then(setMoveRooms).catch(() => undefined);
      }
    }
    finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <Frame info={null}>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-sm font-medium text-slate-700">No encontramos esta reserva.</p>
          <p className="mt-1 text-xs text-slate-400">El enlace puede ser incorrecto o haber expirado.</p>
        </div>
      </Frame>
    );
  }
  if (!data) return <Frame info={null}><p className="text-center text-sm text-slate-400">Cargando…</p></Frame>;

  const st = data.status === 'BOOKED' && pendingPay
    ? { label: 'Apartada — pago pendiente', cls: 'bg-amber-100 text-amber-700' }
    : MANAGE_STATUS[data.status];
  // GH #578 — ventana efectiva + tope de cambios deciden si se puede "Cambiar
  // horario". Dentro de la ventana (D1) o sin cambios disponibles → solo cancelar.
  const effWindowH = data.effectiveWindowHours ?? data.cancelMinHours ?? 0;
  const hoursToStart = (new Date(data.startsAt).getTime() - Date.now()) / 3_600_000;
  const withinWindow = effWindowH > 0 && hoursToStart < effWindowH;
  const movesRemaining = data.rescheduleLimit == null
    ? null
    : Math.max(0, data.rescheduleLimit - (data.rescheduleCount ?? 0));
  const canReschedule = !withinWindow && (movesRemaining === null || movesRemaining > 0);
  return (
    <Frame info={data.business}>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-slate-800">{data.offeringName}</p>
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {fmtDay(data.startsAt)} · {fmtTime(data.startsAt)} · {data.durationMinutes} min
          {data.resourceTypeName ? ` · ${data.resourceTypeName}` : ''}
          {data.spotName ? <> · Lugar: <b>{data.spotName}</b></> : null}
        </p>
        <p className="text-xs text-slate-400">A nombre de {data.customerName}</p>
        {effWindowH > 0 && data.status === 'BOOKED' && (
          <p className="mt-2 text-[11px] text-slate-400">
            Puedes cambiar tu horario hasta {effWindowH} h antes de tu cita.
            {movesRemaining !== null && <> Te quedan <b>{movesRemaining}</b> cambio{movesRemaining === 1 ? '' : 's'} de horario.</>}
          </p>
        )}
        {msg && <p className="mt-3 text-sm text-emerald-700">{msg}</p>}
        {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}

        {/* Epic Pagos — estado de pago + factura (CFDI opcional). */}
        <ManagePaymentSection token={token} status={data.status} onPendingPay={setPendingPay} />

        {data.status === 'BOOKED' && (
          <div className="mt-4 space-y-3">
            {moving ? (
              <div className="space-y-2">
                <select className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">Elige el nuevo horario…</option>
                  {options.map((s) => (
                    <option key={s.slotId} value={s.slotId}>
                      {s.offeringName} · {fmtDay(s.startsAt)} {fmtTime(s.startsAt)} ({s.freeCount} libres)
                    </option>
                  ))}
                </select>
                {/* V93 (D3) — el destino tiene mapa: re-elige tu lugar (el previo
                    viene pre-seleccionado si sigue libre). */}
                {needsSpot && moveRoom && (
                  <div>
                    <p className="text-[12px] font-medium text-slate-500">Elige tu lugar en el nuevo horario</p>
                    <div className="mt-1.5">
                      <SpotMapPicker spots={moveRoom.spots} layout={moveRoom.layout ?? null}
                                     value={moveSpotId} onChange={setMoveSpotId}
                                     disabledAll={moveRoom.freeCount <= 0}
                                     accent={data.business.primaryColor} />
                    </div>
                    <p className="mt-1 text-[10px] text-slate-400">🔒 = ocupado o apartado para la lista de espera.</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button className={btnGhost} onClick={() => setMoving(false)}>Volver</button>
                  <button disabled={busy || !target || (needsSpot && !moveSpotId)} onClick={move}
                    className="flex-1 rounded-xl bg-slate-900 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                    Confirmar cambio
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* PR4 — leyenda fiscal SUAVE para el cliente (la accionable es
                    in-app para el negocio). El CFDI NO se cancela automáticamente. */}
                {cfdiStamped && (
                  <div className="mb-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-800">
                    Tu reserva tiene <b>factura emitida</b>. Si cancelas y necesitas una nota de crédito,
                    el negocio la gestiona con su contador.
                  </div>
                )}
                {/* GH #578 (D1) — dentro de la ventana o sin cambios: se oculta
                    "Cambiar horario"; cancelar SIEMPRE funciona (con retención si aplica). */}
                {data.bookingKey && !canReschedule && (
                  <p className="mb-2 text-[11px] text-slate-400">
                    {withinWindow
                      ? 'Ya estás dentro de la ventana de tu cita: solo puedes cancelar o asistir.'
                      : 'Usaste todos tus cambios de horario: solo puedes cancelar o asistir.'}
                  </p>
                )}
                <div className="flex gap-2">
                  {data.bookingKey && canReschedule && (
                    <button className="flex-1 rounded-xl border border-slate-300 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            onClick={startMove} disabled={busy}>
                      Cambiar horario
                    </button>
                  )}
                  <button className="flex-1 rounded-xl border border-rose-200 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50"
                          onClick={cancel} disabled={busy}>
                    Cancelar reserva
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Frame>
  );
}
